'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { WidgetStyle } from '@/lib/chat-interface/widget-styles';
import {
  chipSurfaceFromWidget,
  suggestedQuestionChipColors,
} from '@/lib/agents/suggested-question-chips';

export type SuggestiveReplyChipsProps = {
  suggestions: string[];
  onSelect: (suggestion: string) => void;
  /** Ally / dashboard use theme tokens; agent widgets can pass widgetStyle. */
  variant?: 'default' | 'widget';
  widgetStyle?: WidgetStyle;
  theme?: 'light' | 'dark';
  /** Drawer questions stack like Fin. Replies stay a wrapping row. */
  layout?: 'row' | 'stack';
  className?: string;
};

export function SuggestiveReplyChips({
  suggestions,
  onSelect,
  variant = 'default',
  widgetStyle = 'white',
  theme = 'light',
  layout = 'row',
  className,
}: SuggestiveReplyChipsProps) {
  if (!suggestions.length) return null;

  const surface =
    variant === 'widget' ? chipSurfaceFromWidget(widgetStyle, theme) : theme === 'dark' ? 'dark' : 'light';
  const colors = suggestedQuestionChipColors(surface);
  const stacked = layout === 'stack';

  return (
    <div
      className={cn(
        'w-full',
        !stacked && 'overflow-x-auto scrollbar-hide -mx-1 px-1',
        className,
      )}
      style={!stacked ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : undefined}
      data-testid="suggestive-replies"
    >
      <div
        className={cn(
          'flex gap-2',
          stacked ? 'flex-col items-start' : 'min-w-min flex-row pb-1',
        )}
      >
        <AnimatePresence initial={false} mode="popLayout">
          {suggestions.map((suggestion) => (
            <motion.button
              key={suggestion}
              type="button"
              layout
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
              onClick={() => onSelect(suggestion)}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-normal',
                stacked ? 'max-w-full text-left' : 'flex-shrink-0 whitespace-nowrap',
              )}
              style={{
                backgroundColor: colors.bg,
                border: 'none',
                color: colors.text,
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = colors.hover;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = colors.bg;
              }}
            >
              {suggestion}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
